import type {
  GetFeedArticlesTableDataArticle,
  GetFeedArticlesTableDataVariables,
} from "@/data-access/discovery-view/queries/discovery-view-queries";
import { GetFeedArticlesTableData } from "@/data-access/discovery-view/queries/discovery-view-queries";
import { client } from "@/lib/urqlProvider";
import {
  transformArticleToRowData,
  transformArticleWithLabels,
} from "@/store/discovery/discovery-content-management/discovery-content-management.helpers";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import type { IDatasource, IGetRowsParams } from "ag-grid-community";
import type { FeedArticleRowData } from "./FeedArticlesTableStates";

/**
 * Fetches feed articles data for SSRM
 * Returns the articles and total count
 */
const fetchArticlesForSSRM = async (
  filters: GetFeedArticlesTableDataVariables["filters"],
  ordering: GetFeedArticlesTableDataVariables["ordering"],
  limit: number,
  offset: number,
): Promise<{
  articles: GetFeedArticlesTableDataArticle[];
  totalCount: number;
}> => {
  const result = await client
    .query(
      GetFeedArticlesTableData,
      {
        ordering,
        filters,
        pagination: {
          limit,
          offset,
        },
      },
      {
        requestPolicy: "network-only",
      },
    )
    .toPromise();

  if (result.error) {
    throw new Error(result.error.message);
  }

  if (!result.data?.feedArticleProjectionsTable) {
    return {
      articles: [],
      totalCount: 0,
    };
  }

  const { totalCount, results: articles } =
    result.data.feedArticleProjectionsTable;

  return {
    articles,
    totalCount: Number(totalCount ?? 0),
  };
};

/**
 * Session id for the current datasource. Incremented when a new datasource is
 * created (e.g. search changed) so in-flight requests from the previous
 * datasource do not clear isFetchingArticles and cause skeleton flicker.
 */
let currentSessionId = 0;

/**
 * In-flight count for the current session only. Only requests from the
 * current datasource session can clear isFetchingArticles.
 */
let inFlightCountForCurrentSession = 0;

/**
 * Tracks the latest fetch so stale responses do not overwrite totalCount.
 */
let latestFetchId = 0;

/**
 * Call when a new datasource is created so old in-flight requests ignore
 * themselves and only the new datasource's requests control loading state.
 */
export const startNewDatasourceSession = () => {
  currentSessionId += 1;
  inFlightCountForCurrentSession = 0;
};

/**
 * Creates the Infinite Row Model datasource for the feed articles table
 * @param getFilters - Function that returns current filters (feedId, dateRange, selectedFilters)
 * @param searchTerm - Current search term (captured at datasource creation time)
 * @param ordering - Current ordering (captured at datasource creation time)
 */
export const createFeedArticlesDatasource = (
  getFilters: () => GetFeedArticlesTableDataVariables["filters"],
  searchTerm: string,
  ordering: GetFeedArticlesTableDataVariables["ordering"],
): IDatasource => {
  return {
    getRows: async (params: IGetRowsParams) => {
      const sessionId = currentSessionId;
      const fetchId = ++latestFetchId;
      inFlightCountForCurrentSession += 1;

      // Get current state from store
      const { setFetchingArticles, setError } =
        useDiscoveryContentManagementStore.getState();

      // Get filters from component context
      const baseFilters = getFilters();
      const filters: GetFeedArticlesTableDataVariables["filters"] = {
        ...baseFilters,
        // Use searchTerm captured at datasource creation time
        ...(searchTerm ? { searchMatch: searchTerm } : {}),
      };

      // Calculate pagination from AG Grid request
      // AG Grid Infinite Row Model provides startRow and endRow
      const startRow = params.startRow ?? 0;
      const endRow = params.endRow ?? 0;

      // Default page size
      const defaultPageSize = 50;

      // Calculate limit and offset
      // For Infinite Row Model, endRow might be undefined on first call
      // We should request at least one page of data
      let limit = defaultPageSize;
      if (endRow > startRow) {
        limit = endRow - startRow;
      } else if (endRow === 0 && startRow === 0) {
        // Initial load - request first page
        limit = defaultPageSize;
      }
      const offset = startRow;

      try {
        setFetchingArticles(true);
        setError(null);

        // Fetch articles
        // Use ordering captured at datasource creation time
        const { articles, totalCount } = await fetchArticlesForSSRM(
          filters,
          ordering,
          limit,
          offset,
        );

        // Get dictionary for transformation
        const dictionary = useFilterDictionary.getState();

        // Transform articles: first add labels, then convert to row data
        const transformedArticles = articles.map((article) =>
          transformArticleWithLabels(article, dictionary),
        );
        const rowData: FeedArticleRowData[] = transformedArticles.map(
          transformArticleToRowData,
        );

        // Stale response: don't update store, but still tell grid and clear in-flight for this session
        if (fetchId !== latestFetchId) {
          if (sessionId === currentSessionId) {
            inFlightCountForCurrentSession -= 1;
            if (inFlightCountForCurrentSession === 0) {
              setFetchingArticles(false);
            }
          }
          params.successCallback(rowData, totalCount);
          return;
        }

        // Update total count in store (for overlay logic and display)
        useDiscoveryContentManagementStore.getState().setTotalCount(totalCount);

        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setFetchingArticles(false);
          }
        }

        // Tell AG Grid the results
        // For Infinite Row Model: successCallback(rowData, lastRow)
        // If lastRow is undefined, grid assumes more rows available
        // If lastRow is set, it's the total row count
        params.successCallback(rowData, totalCount);
      } catch (error: unknown) {
        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setFetchingArticles(false);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            setError(errorMessage);
          }
        }
        params.failCallback();
      }
    },
  };
};
